import React, { useCallback, useRef, useState } from "react";
import styled from "styled-components";
import toast from "react-hot-toast";

import Modal from "../../../components/modal/Modal";
import Input from "../../../components/form-item/Input";
import Button from "../../../components/button/Button";
import ImageEditorModal from "../../../components/image-editor/ImageEditorModal";
import type { CropResult } from "../../../components/image-editor/cropUtils";
import { ASPECT_RULES } from "../../../components/image-editor/aspectRule";
import {
  useCreateAdminMileageRankMutation,
  useUpdateAdminMileageRankMutation,
  type AdminMileageRank,
} from "../../../app/services/adminMileageRanks";

/** プレビュー枠の比率（クロップの制約と揃える） */
const CARD_ASPECT = ASPECT_RULES.mileageCard.ratio;

interface MileageRankModalProps {
  /** 編集対象。null は新規作成 */
  rank: AdminMileageRank | null;
  onClose: () => void;
}

// カード画像プレビュー（216:136）
const CardPreview = styled.div`
  width: 216px;
  aspect-ratio: ${CARD_ASPECT};
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  overflow: hidden;
  background: var(--soremo-background);
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const FieldNote = styled.p.attrs({
  className: "bodytext-11 u-text-light-gray",
})``;

/**
 * マイレージランクの作成・編集モーダル。
 *
 * - カード画像は既存の ImageEditorModal（react-easy-crop）で 216:136 に
 *   クロップしてから multipart で送信する。
 * - `is_default` ランクはボーダーライン（point）を変更できない（表示のみ）。
 * - 更新は取得時の modified_timestamp を楽観ロックとして送る。
 */
const MileageRankModal: React.FC<MileageRankModalProps> = ({
  rank,
  onClose,
}) => {
  const isCreate = rank === null;

  const [rankNameJp, setRankNameJp] = useState(rank?.rank_name_jp ?? "");
  const [rankNameEn, setRankNameEn] = useState(rank?.rank_name_en ?? "");
  const [point, setPoint] = useState(
    rank !== null ? String(rank.point) : "",
  );
  const [usageFee, setUsageFee] = useState(
    rank !== null ? String(rank.usage_fee) : "",
  );

  // 画像: 変更時のみ File を保持（未変更なら送信しない）
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(
    rank?.image ?? null,
  );
  const [editorSrc, setEditorSrc] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [createRank, { isLoading: isCreating }] =
    useCreateAdminMileageRankMutation();
  const [updateRank, { isLoading: isUpdating }] =
    useUpdateAdminMileageRankMutation();
  const isSaving = isCreating || isUpdating;

  const handleFileSelected = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      // 同じファイルを選び直せるよう毎回リセット
      event.target.value = "";
      if (!file) return;
      if (!["image/jpeg", "image/png"].includes(file.type)) {
        toast.error("画像は JPG / PNG 形式のみアップロードできます");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("画像は5MB以下のファイルを指定してください");
        return;
      }
      const reader = new FileReader();
      reader.onload = () => setEditorSrc(String(reader.result));
      reader.readAsDataURL(file);
    },
    [],
  );

  const handleCropConfirm = useCallback((result: CropResult) => {
    setImageFile(result.file);
    setImagePreview(result.base64);
    setEditorSrc(null);
  }, []);

  const handleSave = useCallback(async () => {
    const pointValue = isCreate || !rank?.is_default ? Number(point) : rank.point;
    const usageFeeValue = Number(usageFee);
    if (point.trim() === "" || !Number.isFinite(pointValue)) {
      toast.error("ボーダーラインを数値で入力してください");
      return;
    }
    if (usageFee.trim() === "" || !Number.isFinite(usageFeeValue)) {
      toast.error("利用料率を数値で入力してください");
      return;
    }
    if (usageFeeValue < 0 || usageFeeValue > 100) {
      toast.error("利用料率は 0〜100 の範囲で入力してください");
      return;
    }

    const input = {
      rank_name_jp: rankNameJp,
      rank_name_en: rankNameEn,
      point: pointValue,
      usage_fee: usageFeeValue,
      ...(imageFile ? { image: imageFile } : {}),
    };

    try {
      if (isCreate) {
        await createRank(input).unwrap();
        toast.success("ステージを追加しました");
      } else {
        await updateRank({
          mileageId: rank.mileage_id,
          input: { ...input, modified: rank.modified_timestamp },
        }).unwrap();
        toast.success("ステージを更新しました");
      }
      onClose();
    } catch (err) {
      const message = (err as { data?: { message?: string } })?.data?.message;
      toast.error(message || "保存に失敗しました");
    }
  }, [
    isCreate,
    rank,
    point,
    usageFee,
    rankNameJp,
    rankNameEn,
    imageFile,
    createRank,
    updateRank,
    onClose,
  ]);

  return (
    <>
      <Modal
        title={isCreate ? "新規ステージを追加" : "ステージを編集"}
        titleSize="medium"
        onClose={onClose}
        primaryAction={{
          label: isCreate ? "追加" : "保存",
          onClick: handleSave,
          loading: isSaving,
        }}
        secondaryAction={{
          label: "キャンセル",
          onClick: onClose,
        }}
      >
        <div className="u-col u-gap16 u-w100">
          {/* カード画像 */}
          <div className="u-col u-gap8">
            <span className="bodytext-13">カード画像</span>
            <CardPreview>
              {imagePreview ? (
                <img src={imagePreview} alt="ステージカード画像" />
              ) : (
                <span className="bodytext-11 u-text-light-gray">
                  画像なし
                </span>
              )}
            </CardPreview>
            <div>
              <Button
                size="small"
                theme="tertiary"
                onClick={() => fileInputRef.current?.click()}
              >
                画像を選択
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png"
                style={{ display: "none" }}
                onChange={handleFileSelected}
              />
            </div>
          </div>

          <Input
            label="ステージ名"
            value={rankNameJp}
            onChange={(e) => setRankNameJp(e.target.value)}
            placeholder="例: プラチナ"
          />
          <Input
            label="ステージ名（英）"
            value={rankNameEn}
            onChange={(e) => setRankNameEn(e.target.value)}
            placeholder="例: Platinum"
          />

          {rank?.is_default ? (
            <div className="u-col u-gap4">
              <span className="bodytext-13">ボーダーライン</span>
              <span className="bodytext-15">
                {rank.point.toLocaleString()} マイル
              </span>
              <FieldNote>
                デフォルトステージのボーダーラインは変更できません。
              </FieldNote>
            </div>
          ) : (
            <Input
              label="ボーダーライン"
              type="number"
              value={point}
              onChange={(e) => setPoint(e.target.value)}
              append="マイル"
              placeholder="例: 100000"
            />
          )}

          <Input
            label="利用料率"
            type="number"
            value={usageFee}
            onChange={(e) => setUsageFee(e.target.value)}
            append="%"
            placeholder="例: 17.5"
          />
        </div>
      </Modal>

      {/* 画像クロップ（216:136） */}
      {editorSrc && (
        <ImageEditorModal
          imageSrc={editorSrc}
          aspectRule={ASPECT_RULES.mileageCard}
          outputFileName="mileage-card.jpg"
          onConfirm={handleCropConfirm}
          onCancel={() => setEditorSrc(null)}
        />
      )}
    </>
  );
};

export default MileageRankModal;
